import React, { Fragment, useState, useMemo } from 'react';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { SiMicrosoftexcel } from "react-icons/si";
import * as XLSX from 'xlsx';
import { Button } from './Button';
import { LiaLongArrowAltDownSolid } from "react-icons/lia";
import { LiaLongArrowAltUpSolid } from "react-icons/lia";
import { SpeedDial } from 'primereact/speeddial';

interface TableColumn {
    header: string;
    accessor: string;
    sortable?: boolean;
    action?: boolean;
    width?: number;
}

interface TableProps<T> {
    columns: TableColumn[];
    data: T[];
    onEdit?: (row: T) => void;
    onDelete?: (row: T) => void; // Optional
    onNext?: () => void;
    onPrev?: () => void;
    onAll?: () => void;
    width?: number;  // Optional width property

}

const AppTable = <T,>({ columns, data, onEdit, onDelete, onNext, onAll, onPrev }: TableProps<T>) => {
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'ascending' | 'descending' } | null>(null);
    const [globalSearchQuery, setGlobalSearchQuery] = useState('');

    const getSortValue = (value: any) => {
        if (typeof value === 'string') {
            return value.toLowerCase();
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return value;
        }
        return 0;
    };

    const filteredAndSortedData = useMemo(() => {
        let filteredItems = data.filter(row => {
            return columns.some(column => {
                const cellValue = String((row as any)[column.accessor]).toLowerCase();
                return cellValue.includes(globalSearchQuery.toLowerCase());
            });
        });

        if (sortConfig !== null) {
            const { key, direction } = sortConfig;
            filteredItems.sort((a, b) => {
                const aValue = getSortValue((a as any)[key]);
                const bValue = getSortValue((b as any)[key]);

                if (aValue < bValue) {
                    return direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return filteredItems;
    }, [data, globalSearchQuery, sortConfig, columns]);

    const handleSort = (key: string) => {
        let direction: 'ascending' | 'descending' = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const hasActionColumn = columns.some(column => column.action);

    const exportToExcel = () => {
        const sortableColumns = columns.filter(column => column.accessor);
        const exportData = filteredAndSortedData.map((row) => {
            const rowData: any = {};
            sortableColumns.forEach((column) => {
                rowData[column.header] = (row as any)[column.accessor];
            });
            return rowData;
        });
        const worksheet = XLSX.utils.json_to_sheet(exportData, { header: sortableColumns.map(col => col.header) });
        const headerRange = XLSX.utils.decode_range(worksheet['!ref'] || '');
        if (headerRange && headerRange.s && headerRange.e) {
            for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
                if (worksheet[cellAddress]) {
                    worksheet[cellAddress].s = { font: { bold: true } };
                }
            }
        }
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        // Export workbook to an Excel file
        XLSX.writeFile(workbook, 'SortableColumnsData.xlsx');
    };

    return (
        <Fragment>
            {/* Global search box */}
            <div className="flex p-1 justify-between border-b-[2px] dark:border-b-[5px] border-gray-100 dark:border-[#0c4a6e90] bg-slate-200 dark:bg-[#0c4a6e90] rounded-tl-md rounded-tr-md pt-1 items-center">
                <input
                    type="text"
                    placeholder="Search."
                    value={globalSearchQuery}
                    onChange={(e) => setGlobalSearchQuery(e.target.value)}
                    className="w-1/3 px-3 py-1 text-[12px] dark:bg-transparent dark:ring-sky-800 dark:text-gray-300 ring-[1px] ring-gray-200 rounded-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                />
                {/* Export to Excel button */}
                <button
                    onClick={exportToExcel}
                    className="p-1.5 bg-slate-600 text-white text-sm rounded-full shadow-sm hover:bg-slate-700 focus:outline-none"
                >
                    <SiMicrosoftexcel />
                </button>
            </div>
            <div className='overflow-x-auto overflow-auto overflow-y-auto h-[78vh]'>
                <table className="min-w-full divide-y divide-gray-200 dark:divide-sky-800">
                    <thead className="">

                        <tr className='top-0 sticky z-50 whitespace-nowrap bg-slate-200 dark:bg-green-900'>
                            {hasActionColumn && (
                                <th className="px-4 py-2 border-b-[5px] border-slate-100 dark:border-[#0c4a6e90] text-left text-[12px] font-semibold text-gray-700 dark:text-gray-200 capitalize tracking-wider">
                                    Actions
                                </th>
                            )}
                            {columns.filter(item => !item.action).map((column) => (
                                <th
                                    key={column.accessor}
                                    className={`relative border-b-[5px] w-${column.width} border-slate-100 dark:border-[#0c4a6e90] px-4 py-2 text-left text-[12px] font-semibold text-gray-700 dark:text-gray-200 capitalize tracking-wider`}

                                >
                                    <div className="flex items-center">
                                        {column.header}
                                        {column.sortable && (
                                            <span className='select-none text-[12px] cursor-pointer' onClick={() => handleSort(column.accessor)}>
                                                {sortConfig && sortConfig.key === column.accessor
                                                    ? sortConfig.direction === 'ascending'
                                                        ? <LiaLongArrowAltDownSolid />
                                                        : <LiaLongArrowAltUpSolid />
                                                    : <LiaLongArrowAltUpSolid />} {/* Default value when no sorting applied */}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}

                        </tr>
                    </thead>

                    <tbody>
                        {filteredAndSortedData.map((row, rowIndex) => {
                            const typedRow = row as { active: number } & T; // Narrow down the type
                            return (
                                <tr key={rowIndex} className={`border-b  ${typedRow?.active === 1 ? 'border-green-500 border-l-2' : typedRow?.active === 0 ? 'border-red-500 border-l-2' : typedRow?.active === 2 ? "border-yellow-500 border-l-2" : ""}  bg-white dark:bg-sky-950 border-gray-300 rounded-md hover:bg-slate-50 dark:hover:bg-green-900 hover:cursor-pointer`}>
                                    {hasActionColumn && (
                                        <td className="px-4 py-1.5 my-auto border-b-[5px] border-slate-100 dark:border-sky-900 whitespace-nowrap text-[12px] font-medium rounded-t-md">
                                            <div className="flex items-center justify-start space-x-4">
                                                {onEdit && (
                                                    <button
                                                        className="hover:text-green-900 p-1 border border-dashed shadow-lg bg-green-600 rounded-full"
                                                        onClick={() => onEdit(row)}
                                                    >
                                                        <div className='flex items-center rounded-full'>
                                                            <MdOutlineEdit size={16} className='text-white' />
                                                        </div>
                                                    </button>
                                                )}
                                                {onDelete && (
                                                    <button
                                                        className="text-red-600 hover:text-red-900"
                                                        onClick={() => onDelete(row)}
                                                    >
                                                        <div className='flex items-center'>
                                                            <RiDeleteBin6Line size={15} />
                                                            <h6 className='ml-1'>Delete</h6>
                                                        </div>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    )}

                                    {columns.filter(item => !item.action).map((column) => (
                                        <td key={column.accessor} className={`px-4 py-2 border-b-[5px] font-public-sans border-slate-100 dark:text-slate-400 dark:border-sky-900 whitespace-nowrap text-[12px] text-gray-600 rounded-t-md`}>
                                            {(row as any)[column.accessor]}
                                        </td>
                                    ))}

                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className='bg-slate-100 dark:bg-transparent flex flex-row gap-2 justify-end'>
                {onPrev && (
                    <Button btn_type='prev' onClick={onPrev} el='button' />
                )}
                {/* {onAll && (
                    <Button btn_type='all' onClick={onAll} el='button' />
                )} */}
                {onNext && (
                    <Button btn_type='next' onClick={onNext} el='button' />
                )}
            </div>
        </Fragment>
    );
};

export default AppTable;
